// Shared QR-code section for the staff forms. Presentational + controlled: the
// orchestrator owns qrCodeUrl + the generate callback. One copy for zone /
// exhibit — `ownerNoun` + `entityId` differ only to build the download filename
// (`${ownerNoun}-${entityId}-qr.png`) (review #8; replaced the near-identical
// zones/ + exhibits/ pair).

import type { FC } from "react";
import { Download } from "lucide-react";
import { Button } from "@shared/components/ui/button";

export interface QRSectionProps {
  ownerNoun: string;
  entityId: string;
  qrCodeUrl?: string | null;
  onGenerate: () => void | Promise<void>;
  generating?: boolean;
}

const QRSection: FC<QRSectionProps> = ({
  ownerNoun,
  entityId,
  qrCodeUrl,
  onGenerate,
  generating = false,
}) => {
  const handleDownload = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = `${ownerNoun}-${entityId}-qr.png`;
    link.click();
  };

  return (
    <div
      className="space-y-4 p-6 bg-white border border-border rounded-lg"
      data-testid="qr-section"
    >
      <h3 className="font-heading font-semibold">QR Code</h3>
      {qrCodeUrl ? (
        <div className="flex items-start gap-4">
          <div className="p-4 bg-white border border-border rounded-lg">
            <img
              src={qrCodeUrl}
              alt="QR Code"
              className="w-32 h-32"
              data-testid="qr-code-preview"
            />
          </div>
          <Button
            type="button"
            onClick={handleDownload}
            variant="outline"
            data-testid="download-qr-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Download QR Code
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          onClick={onGenerate}
          variant="outline"
          disabled={generating}
          data-testid="generate-qr-button"
        >
          {generating ? "Generating…" : "Generate QR Code"}
        </Button>
      )}
    </div>
  );
};

export default QRSection;
